/**
 * Normalizes field `dataType` between UI (spaces, SQL-like labels)
 * and API / MySQL ENUM codes (underscores).
 */

/** Send to backend: spaces become underscores (e.g. PRIMARY KEY → PRIMARY_KEY). */
export function fieldCodeToApi(code: string | null | undefined): string {
  if (code == null || code === '') {
    return '';
  }
  return String(code).trim().replace(/\s+/g, '_');
}

/** Show in UI: underscores become spaces (e.g. PRIMARY_KEY → PRIMARY KEY). */
export function fieldCodeToDisplay(code: string | null | undefined): string {
  if (code == null || code === '') {
    return '';
  }
  return String(code).trim().replace(/_/g, ' ');
}

/**
 * Title case for field codes: underscores/spaces into words, e.g. PRIMARY_KEY → Primary Key.
 * Purely numeric tokens are left as-is (e.g. VARCHAR_255 → Varchar 255).
 */
export function fieldCodeToTitleCase(codeOrSpaced: string | null | undefined): string {
  if (codeOrSpaced == null || codeOrSpaced === '') {
    return '';
  }
  return String(codeOrSpaced)
    .trim()
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) {
        return word;
      }
      if (/^\d+$/.test(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Master tables (data types) LABEL column: same as code, title case, or "-" if missing.
 */
export function masterTableCodeToLabelColumn(code: string | null | undefined): string {
  if (code == null || code === '') {
    return '-';
  }
  return fieldCodeToTitleCase(code);
}

/**
 * Master tables (data types): display mask — API may use 0-based sort; show as 1-based in the table.
 */
export function masterTableSortOrderDisplay(sortOrder: number | null | undefined): string {
  if (sortOrder == null || !Number.isFinite(Number(sortOrder))) {
    return '-';
  }
  return String(Number(sortOrder) + 1);
}

/**
 * Single canonical form for matching CSV / UI values to master table `code` (spaces → _, uppercase).
 */
export function normalizeMasterCodeForMatch(raw: string): string {
  return fieldCodeToApi(raw).toUpperCase();
}

/**
 * Set of allowed normalized codes from master table rows (datatypes, …).
 */
export function buildMasterTableAllowedCodeSet(codes: readonly { code: string }[]): Set<string> {
  const set = new Set<string>();
  for (const row of codes) {
    if (row?.code) {
      set.add(normalizeMasterCodeForMatch(row.code));
    }
  }
  return set;
}

/**
 * Whether a non-empty value matches a master code; empty is allowed or rejected by caller.
 */
export function isRawInMasterCodeSet(
  raw: string | undefined,
  { allowEmpty, allowed }: { allowEmpty: boolean; allowed: Set<string> }
): boolean {
  if (raw == null || raw === '') {
    return allowEmpty;
  }
  return allowed.has(normalizeMasterCodeForMatch(raw));
}
